import React from 'react';
import { useTheme } from '../context/ThemeContext';

interface CanvasRulerProps {
  orientation: 'horizontal' | 'vertical';
  scale: number;
  offset: number;
  length: number;
  unit?: 'px' | 'cm' | 'mm';
}

const calculateDynamicInterval = (scale: number): { major: number; medium: number; minor: number } => {
  if (scale >= 8) {
    return { major: 500, medium: 100, minor: 20 };
  } else if (scale >= 4) {
    return { major: 500, medium: 100, minor: 20 };
  } else if (scale >= 2) {
    return { major: 500, medium: 100, minor: 20 };
  } else if (scale >= 1) {
    return { major: 500, medium: 100, minor: 20 };
  } else if (scale >= 0.5) {
    return { major: 500, medium: 100, minor: 50 };
  } else if (scale >= 0.25) {
    return { major: 500, medium: 100, minor: 100 };
  } else if (scale >= 0.1) {
    return { major: 500, medium: 500, minor: 500 };
  }

  return { major: 1000, medium: 1000, minor: 1000 };
};

export const CanvasRuler: React.FC<CanvasRulerProps> = ({
  orientation,
  scale,
  offset,
  length,
}) => {
  const { isDark } = useTheme();
  const isHorizontal = orientation === 'horizontal';

  const rulerBg = 'var(--map-ruler-bg)';
  const tickColorMajor = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.1)';
  const tickColorMinor = isDark ? 'rgba(255,255,255,0.11)' : 'rgba(0,0,0,0.06)';
  const textColor = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.55)';
  const zeroLineColor = isDark ? '#3b82f6' : 'rgba(0,0,0,0.18)';
  const borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const rulerSize = 20;

  const intervals = calculateDynamicInterval(scale);
  const majorInterval = intervals.major;
  const mediumInterval = intervals.medium;
  const minorInterval = intervals.minor;

  const viewportStartLogical = (0 - offset) / scale;
  const viewportEndLogical = (length - offset) / scale;

  const firstMark = Math.floor(viewportStartLogical / minorInterval) * minorInterval;
  const lastMark = Math.ceil(viewportEndLogical / minorInterval) * minorInterval;

  const marks: React.ReactElement[] = [];

  const zeroScreenPos = Math.round(0 * scale + offset) + 0.5;
  marks.push(
    <line
      key="zero-line"
      x1={isHorizontal ? zeroScreenPos : 0}
      y1={isHorizontal ? 0 : zeroScreenPos}
      x2={isHorizontal ? zeroScreenPos : rulerSize}
      y2={isHorizontal ? rulerSize : zeroScreenPos}
      stroke={zeroLineColor}
      strokeWidth={1}
      opacity={0.34}
    />,
  );

  for (let logicalPos = firstMark; logicalPos <= lastMark; logicalPos += minorInterval) {
    const screenPos = Math.round(logicalPos * scale + offset) + 0.5;

    if (screenPos < -10 || screenPos > length + 10) continue;

    const isMajor = Math.abs(logicalPos % majorInterval) < 0.001;
    const isMedium = Math.abs(logicalPos % mediumInterval) < 0.001;

    let markLength: number;
    let showLabel = false;
    let strokeWidth = 1;
    let tickColor: string;

    if (isMajor) {
      markLength = 16;
      strokeWidth = 1;
      tickColor = tickColorMajor;
      showLabel = logicalPos % 100 === 0;
    } else if (isMedium) {
      markLength = 12;
      strokeWidth = 0.65;
      tickColor = tickColorMajor;
      showLabel = logicalPos % 100 === 0;
    } else {
      markLength = 7;
      strokeWidth = 0.3;
      tickColor = tickColorMinor;
    }

    if (isHorizontal) {
      marks.push(
        <line
          key={`mark-${logicalPos}`}
          x1={screenPos}
          y1={rulerSize}
          x2={screenPos}
          y2={rulerSize - markLength}
          stroke={tickColor}
          strokeWidth={strokeWidth}
          strokeLinecap="square"
        />,
      );

      if (showLabel) {
        marks.push(
          <text
            key={`label-${logicalPos}`}
            x={logicalPos === 0 ? screenPos + 2 : screenPos + 3}
            y={5}
            fontSize="8px"
            fontWeight="400"
            fontFamily="system-ui, -apple-system, 'Segoe UI', Arial, sans-serif"
            fill={textColor}
            textAnchor="start"
            dominantBaseline="hanging"
            textRendering="optimizeLegibility"
            style={{ userSelect: 'none', pointerEvents: 'none', letterSpacing: '0.01em' }}
          >
            {Math.round(logicalPos)}
          </text>,
        );
      }
    } else {
      marks.push(
        <line
          key={`mark-${logicalPos}`}
          x1={rulerSize}
          y1={screenPos}
          x2={rulerSize - markLength}
          y2={screenPos}
          stroke={tickColor}
          strokeWidth={strokeWidth}
          strokeLinecap="square"
        />,
      );

      if (showLabel) {
        const textX = logicalPos === 0 ? 10.5 : 11;
        const textY = screenPos + 3;

        marks.push(
          <text
            key={`label-${logicalPos}`}
            x={textX}
            y={textY}
            fontSize="8px"
            fontWeight="400"
            fontFamily="system-ui, -apple-system, 'Segoe UI', Arial, sans-serif"
            fill={textColor}
            textAnchor="end"
            dominantBaseline="middle"
            textRendering="optimizeLegibility"
            transform={`rotate(-90 ${textX} ${textY})`}
            style={{ userSelect: 'none', pointerEvents: 'none', letterSpacing: '0.01em' }}
          >
            {Math.round(logicalPos)}
          </text>,
        );
      }
    }
  }

  if (isHorizontal) {
    return (
      <div
        className="absolute left-0 right-0"
        style={{
          height: `${rulerSize}px`,
          zIndex: 35,
          userSelect: 'none',
          backgroundColor: rulerBg,
          border: 'none',
          boxShadow: 'none',
          outline: 'none',
          margin: 0,
          padding: 0,
        }}
      >
        <svg
          width="100%"
          height={rulerSize}
          style={{ display: 'block', border: 'none', outline: 'none' }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="100%" height={rulerSize} fill={rulerBg} />
          <rect x={0} y={rulerSize - 1} width="100%" height={1} fill={borderColor} />
          {marks}
        </svg>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: `${rulerSize}px`,
        zIndex: 35,
        userSelect: 'none',
        backgroundColor: rulerBg,
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
        margin: 0,
        padding: 0,
      }}
    >
      <svg
        width={rulerSize}
        height="100%"
        style={{ display: 'block', border: 'none', outline: 'none', margin: 0, padding: 0 }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width={rulerSize} height="100%" fill={rulerBg} />
        <rect x={rulerSize - 1} y={0} width={1} height="100%" fill={borderColor} />
        {marks}
      </svg>
    </div>
  );
};

export default CanvasRuler;
